import { Button, Grid, Typography } from '@mui/material'
import keyBy from 'lodash/keyBy'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'react-i18next'
import { useAvailableOrganisations } from '@omnea-app/fe-auth/useAvailableOrganisations'
import { usePotentiallyAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useSwitchOrganisation } from '@omnea-app/fe-auth/useSwitchOrganisation'
import { FullWidthLoadingFallback } from '@omnea-app/frontend/components/shared/Loading/components/LoadingFallbacks'
import { TranslationFile } from '@omnea-app/packages/universal/i18n/translationFiles'
import { NotFoundBoundary } from './NotFoundBoundary'

// 👇 Add translations for each resource type to the translation file
type ResourceTypeStringKey = Extract<keyof TranslationFile<'common'>['organisation-switch-boundary']['resourceTypes'], string>

interface Props {
  correctOrganisationId: string
  resourceType: ResourceTypeStringKey
  /** Path to redirect to after switching. If not provided, defaults to home. */
  redirectTo?: string
}

export const OrganisationSwitchBoundary: React.FC<Props> = ({ correctOrganisationId, resourceType, redirectTo }) => {
  const currentSession = usePotentiallyAuthenticatedUser()
  const { t } = useTranslation('common')
  const { reload } = useRouter()

  const { data, isLoading, isError } = useAvailableOrganisations()

  const switchOrganisation = useSwitchOrganisation()

  const handleClick = async () => {
    await switchOrganisation(correctOrganisationId, { redirectTo })
    reload()
  }

  if (isLoading) {
    return <FullWidthLoadingFallback />
  }

  if (isError || !data || !currentSession.org) {
    return <NotFoundBoundary />
  }

  const availableOrganisationsById = keyBy(data.availableOrganisations, 'id')
  const currentOrg = availableOrganisationsById[currentSession.org.organisationId]
  const correctOrg = availableOrganisationsById[correctOrganisationId]

  if (!currentOrg || !correctOrg) {
    return <NotFoundBoundary />
  }

  const resourceTypeString = t(`organisation-switch-boundary.resourceTypes.${resourceType}`)

  return (
    <Grid container spacing={0} direction="column" alignItems="center" justifyContent="center" style={{ minHeight: '100vh' }}>
      <Grid
        container
        direction="column"
        sx={(theme) => ({
          backgroundColor: theme.palette.bg.surface.elevated,
          borderRadius: '8px',
          boxShadow: theme.customShadows.sm,
          alignItems: 'center',
          textAlign: 'center',
          padding: '40px',
          width: '480px',
        })}
      >
        <Image
          src="/app/assets/organisation-switch.png"
          alt="error"
          width={1536}
          height={1024}
          style={{ width: 250, height: 'auto' }}
        />
        <Grid>
          <Typography sx={{ mt: '30px', color: 'text.default', fontSize: '25px', fontWeight: 600 }}>
            <Trans
              i18nKey="common:organisation-switch-boundary.title"
              components={{
                bold: <b />,
              }}
              values={{
                type: resourceTypeString,
                correctOrganisationName: correctOrg.name,
              }}
            />
          </Typography>
        </Grid>
        <Grid>
          <Typography sx={{ mt: '15px', color: 'text.muted', fontSize: '14px', fontWeight: 400 }}>
            <Trans
              i18nKey="common:organisation-switch-boundary.subtext1"
              components={{
                bold: <b />,
              }}
              values={{
                type: resourceTypeString,
                currentOrganisationName: currentOrg.name,
              }}
            />
          </Typography>
        </Grid>
        <Grid>
          <Typography sx={{ mt: '15px', color: 'text.muted', fontSize: '14px', fontWeight: 400 }}>
            {t('organisation-switch-boundary.subtext2')}
          </Typography>
        </Grid>
        <Button
          sx={{
            mt: '20px',
            '&:focus': {
              animation: 'pulse 1.25s infinite',
            },
            '@keyframes pulse': pulseKeyframes,
          }}
          variant="contained"
          onClick={handleClick}
          autoFocus
        >
          {t('organisation-switch-boundary.button', { correctOrganisationName: correctOrg.name })}
        </Button>
      </Grid>
    </Grid>
  )
}

const pulseKeyframes = {
  '0%': {
    boxShadow: '0 0 0 0 rgba(16, 185, 129, 1)',
  },
  '80%': {
    boxShadow: '0 0 0 8px rgba(16, 185, 129, 0)',
  },
  '100%': {
    boxShadow: '0 0 0 0 rgba(16, 185, 129, 0)',
  },
}
